import { Badge } from "@veil/ui/components/badge";
import CheckCircle2 from "lucide-react/dist/esm/icons/check-circle-2";
import Loader2 from "lucide-react/dist/esm/icons/loader-2";
import XCircle from "lucide-react/dist/esm/icons/x-circle";
import { useEffect, useRef } from "react";

export type ApplyStatus = "running" | "succeeded" | "failed" | null;

/**
 * Streaming apply log. Subscribes to a `logVersion` counter rather than the
 * full log string so the autoscroll effect doesn't re-run when consumers
 * pass through identical-length buffers - see the
 * `rerender-use-ref-transient-values` rule.
 */
export function ApplyLogView({
	log,
	status,
	logVersion,
}: {
	log: string;
	status: ApplyStatus;
	logVersion: number;
}) {
	const ref = useRef<HTMLPreElement>(null);
	useEffect(() => {
		if (ref.current) {
			ref.current.scrollTop = ref.current.scrollHeight;
		}
	}, [logVersion]);
	return (
		<div className="flex flex-col gap-4">
			<div className="flex items-center gap-2 text-sm font-medium">
				{status === "running" && (
					<>
						<Loader2 className="h-4 w-4 animate-spin" />
						<span>Running…</span>
					</>
				)}
				{status === "succeeded" && (
					<>
						<CheckCircle2 className="h-4 w-4 text-emerald-600" />
						<span>Succeeded</span>
					</>
				)}
				{status === "failed" && (
					<>
						<XCircle className="h-4 w-4 text-destructive" />
						<Badge variant="destructive">Failed</Badge>
					</>
				)}
			</div>
			<div className="overflow-hidden rounded-lg border border-zinc-800/90 ring-1 ring-zinc-950/80">
				<pre
					ref={ref}
					className="max-h-[min(58vh,38rem)] overflow-auto bg-zinc-950 p-4 font-mono text-[0.8125rem] leading-relaxed text-zinc-100 [scrollbar-gutter:stable]"
				>
					{log || (status === "running" ? "Waiting for output…" : "(no log)")}
				</pre>
			</div>
		</div>
	);
}
